import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { buildMonthGrid, localDayKey, WEEKDAYS } from '../../lib/calendarGrid';
import { cn } from '../../lib/cn';
import { CalendarPostCard } from '../posts/CalendarPostCard';

/**
 * Condensed month calendar for the composer's Schedule tab. Shows how many posts are already
 * scheduled/published each day (a small count badge) so the user can see a busy/quiet day at a
 * glance, and clicking a (today-or-later) day calls `onPickDay(localIso)` to manually pick it. It
 * does NOT render full post cards — it's a picker, not the main calendar.
 */
export function SchedulePickerCalendar({
  tenantKey,
  selectedDay,
  onPickDay,
}: {
  tenantKey: string;
  /** The currently-picked local day (YYYY-MM-DD), highlighted. */
  selectedDay: string | null;
  onPickDay: (localIso: string) => void;
}) {
  const today = new Date();
  const todayKey = localDayKey(today.toISOString());
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [params, setParams] = useSearchParams();

  const openPost = (postId: string) => {
    const next = new URLSearchParams(params);
    next.set('compose', postId);
    setParams(next);
  };

  // Page to the selected day's month so an already-scheduled post's day is visible/highlighted when
  // the tab opens. Dep is only selectedDay, so manual paging (which doesn't change it) isn't reverted.
  useEffect(() => {
    if (!selectedDay) return;
    const [y, m] = selectedDay.split('-').map(Number);
    if (Number.isFinite(y) && Number.isFinite(m)) {
      setYear(y);
      setMonth(m - 1);
    }
  }, [selectedDay]);

  const grid = useMemo(() => buildMonthGrid(year, month), [year, month]);

  const { data, isLoading } = useQuery({
    queryKey: ['calendar', tenantKey, grid.from, grid.to, 'all'],
    queryFn: ({ signal }) => api.getCalendar(grid.from, grid.to, undefined, signal),
    enabled: tenantKey !== '',
  });

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of data ?? []) {
      const key = localDayKey(item.scheduledAt);
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return map;
  }, [data]);

  // Posts on the selected day (earliest first) — shown in the list below the grid. Only meaningful
  // when the selected day is within the month currently loaded (`data` is that month's window).
  const dayItems = useMemo(() => {
    if (!selectedDay) return [];
    return (data ?? [])
      .filter((it) => localDayKey(it.scheduledAt) === selectedDay)
      .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
  }, [data, selectedDay]);
  const dayInView = selectedDay != null && selectedDay >= grid.from && selectedDay <= grid.to;
  const selectedLabel = selectedDay
    ? new Date(`${selectedDay}T00:00:00`).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
    : '';

  const monthLabel = new Date(year, month).toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
  });
  const step = (delta: number) => {
    const next = new Date(year, month + delta);
    setYear(next.getFullYear());
    setMonth(next.getMonth());
  };

  return (
    <div className="space-y-3">
    <div className="rounded-xl border border-line bg-card p-3 shadow-card">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-ink">{monthLabel}</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label="Previous month"
            className="focus-ring rounded-md px-2 py-0.5 text-ink-muted hover:bg-chip"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            aria-label="Next month"
            className="focus-ring rounded-md px-2 py-0.5 text-ink-muted hover:bg-chip"
          >
            ›
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 text-center text-[0.625rem] font-semibold uppercase tracking-wide text-ink-faint">
        {WEEKDAYS.map((d) => (
          <div key={d} className="py-1">
            {d[0]}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {grid.days.map((cell) => {
          const c = counts.get(cell.iso);
          const isPast = cell.iso < todayKey;
          const selected = selectedDay === cell.iso;
          return (
            <button
              key={cell.iso}
              type="button"
              disabled={isPast}
              onClick={() => onPickDay(cell.iso)}
              className={cn(
                'focus-ring relative flex h-9 flex-col items-center justify-center rounded-md text-xs',
                selected
                  ? 'bg-action font-semibold text-action-fg'
                  : cell.isToday
                    ? 'bg-tint font-semibold text-tint-fg'
                    : cell.inMonth
                      ? 'text-ink-strong hover:bg-chip'
                      : 'text-ink-ghost hover:bg-hover',
                isPast && 'cursor-not-allowed opacity-40 hover:bg-transparent',
              )}
            >
              <span>{cell.date.getDate()}</span>
              {c ? (
                <span
                  className={cn(
                    'mt-0.5 inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full px-1 text-[0.5625rem] font-semibold leading-none',
                    selected ? 'bg-action-fg/25 text-action-fg' : 'bg-line text-ink-label',
                  )}
                >
                  {c}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>

      {/* Posts already on the selected day — awareness while picking a slot. */}
      {dayInView && (
        <div className="rounded-xl border border-line bg-card p-3 shadow-card">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
            {selectedLabel}
          </p>
          {isLoading ? (
            <p className="text-sm text-ink-faint">Loading…</p>
          ) : dayItems.length === 0 ? (
            <p className="text-sm text-ink-muted">No posts scheduled on this day.</p>
          ) : (
            <div className="space-y-1.5">
              {dayItems.map((it) => (
                <CalendarPostCard key={it.id} item={it} onOpen={() => openPost(it.postId)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
